import type { InvoiceTotalsMonths } from '@cocoonspace/sdk-js/types/invoiceTotalsMonths'
import type { NextPage } from 'next'
import {
	useGetInvoiceTotals,
	useListInvoices,
} from '@cocoonspace/sdk-js/domains/invoices/invoices'
import { useDebounce } from '@cocoonspace/shared/hooks/use-debounce.hook'
import { buttonVariants } from '@cocoonspace/ui/components/button'
import { Card } from '@cocoonspace/ui/components/card'
import {
	Table,
	TableBody,
	TableCell,
	TableFooter,
	TableRow,
} from '@cocoonspace/ui/components/table'
import { Trans, useLingui } from '@lingui/react/macro'
import { keepPreviousData } from '@tanstack/react-query'
import { LocalizedLink as Link } from '~/components/localized-link'

import { MainContainer } from '~/components/atoms/main-container'
import { SectionHeader } from '~/components/atoms/section-header'
import { AccountBreadcrumb } from '~/domains/account/components/account-breadcrumb'
import { useFormatPrice } from '~/hooks/use-format-price'
import { InvoiceFilters } from './invoice-filters'
import { InvoiceMonthRow } from './invoice-month-row'
import { InvoiceTableRow } from './invoice-table-row'
import { InvoicesTableHeader } from './invoices-table-header'
import { YearRecap } from './invoices-year-recap'
import { useInvoicesFilters } from './use-invoices-filters.hook'

const InvoicesList = () => {
	const formatPrice = useFormatPrice()
	const [filters] = useInvoicesFilters()

	const { data: invoicesTotal } = useGetInvoiceTotals(
		{ year: filters.activeYear },
		{ query: { placeholderData: keepPreviousData } },
	)

	const { data: invoices } = useListInvoices(
		{ year: filters.activeYear },
		{ query: { placeholderData: keepPreviousData } },
	)

	const yearTotalWithVat =
		invoices?.data?.reduce(
			(total, invoice) => total + invoice.amount_with_vat,
			0,
		) ?? 0

	return (
		<Card className='overflow-hidden'>
			<Table className='w-full'>
				<InvoicesTableHeader />

				<TableBody>
					{Array.from({ length: 12 })
						.map((_, index) => index)
						.sort((a, b) => {
							if (filters.sortOrder === 'asc') {
								return a > b ? 1 : -1
							}

							return a > b ? -1 : 1
						})
						.map((index) => (
							<InvoiceMonthRow
								key={index}
								month={index + 1}
								monthTotal={
									invoicesTotal?.data.months?.[
										(index + 1) as unknown as keyof InvoiceTotalsMonths
									] || 0
								}
								rows={invoices?.data.filter(
									(invoice) => Number(invoice.date.split('-')[1]) === index + 1,
								)}
								year={filters.activeYear}
							/>
						))}
				</TableBody>

				<TableFooter>
					<TableRow>
						<TableCell colSpan={4}>Total {filters.activeYear}</TableCell>

						<TableCell className='text-right'>
							<span className='font-bold'>
								{formatPrice(invoicesTotal?.data.total ?? 0, 'EUR')}{' '}
							</span>
							<span className='text-xxs'>HT</span>
						</TableCell>

						<TableCell className='text-right'>
							<span className='font-bold'>
								{formatPrice(yearTotalWithVat, 'EUR')}{' '}
							</span>
							<span className='text-xxs'>TTC</span>
						</TableCell>

						<TableCell />
					</TableRow>
				</TableFooter>
			</Table>
		</Card>
	)
}

const InvoicesResultsList = () => {
	const [filters] = useInvoicesFilters()

	const ref = useDebounce(filters.searchTerms, 300)

	const { data: invoices } = useListInvoices(
		{ ref },
		{ query: { placeholderData: keepPreviousData } },
	)

	return (
		<Card className='overflow-hidden'>
			<Table className='w-full'>
				<InvoicesTableHeader />

				<TableBody>
					{invoices?.data?.map((invoice) => (
						<InvoiceTableRow
							invoice={invoice}
							key={invoice.ref}
						/>
					))}
				</TableBody>
			</Table>
		</Card>
	)
}

const FaqLink = ({ children }: { children: React.ReactNode }) => (
	<Link
		className={buttonVariants({
			className: 'mt-[2px]',
			size: 'sm',
			variant: 'link',
		})}
		data-cy='faq-link'
		href='https://support.cocoon-space.com'
		target='_blank'
	>
		{children}
	</Link>
)

export const InvoicesPage: NextPage = () => {
	const { t } = useLingui()
	const [filters] = useInvoicesFilters()
	const searchTerms = useDebounce(filters.searchTerms, 300)
	const hasSearchTerms = !!searchTerms?.length

	return (
		<MainContainer>
			<AccountBreadcrumb activePageTitle={t`Mes factures`} />

			<SectionHeader
				title={t`Mes factures`}
				subtitle={t`Consultez et téléchargez vos factures`}
			/>

			<div>
				<Card className='!inline w-auto rounded-sm px-4 py-2 text-sm'>
					<Trans>
						✍️ Pour en savoir plus sur le délai d'édition de vos factures{' '}
						<FaqLink>consultez notre FAQ</FaqLink>.
					</Trans>
				</Card>
			</div>

			<YearRecap />
			<InvoiceFilters />
			{hasSearchTerms ? <InvoicesResultsList /> : <InvoicesList />}
		</MainContainer>
	)
}
